import { CreditCard, Heart } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ReadPoliceReport } from '@/services/api/generated/policeReportsService';
import { useGetMedicalProviderQuery } from '@/services/api/treatmentService';
import { TreatmentRecordRequest } from '@/services/types/client-intake-types';
import {
  CoverageCategory,
  CoverageStub,
} from '@/services/types/coverage-types';
import { MedicalProviderStub } from '@/services/types/treatment-types';

interface LinkedEntityBadgeProps {
  coverage?: CoverageStub;
  medicalNote?: MedicalProviderStub;
  policeReport?: ReadPoliceReport;
  treatmentRequest?: TreatmentRecordRequest;
  // Optional override for contexts without a route matter_id (e.g., /tasks)
  matterId?: string;
}

/**
 * Format an ISO date string (YYYY-MM-DD) to MM/DD/YYYY for display.
 * Returns the original value if it is not in ISO format (e.g. "Present").
 */
const formatDateForDisplay = (value: string): string => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
  }
  return value;
};

const TreatmentRequestBadge = ({
  treatmentRequest,
  matterId,
}: {
  treatmentRequest: TreatmentRecordRequest;
  matterId?: string;
}) => {
  // If we don't have a matterId (e.g., global Tasks route), do not query
  const { data: provider, isLoading } = useGetMedicalProviderQuery(
    { matterId: matterId as string, providerId: treatmentRequest.provider },
    { skip: !matterId },
  );

  if (isLoading) {
    return (
      <Badge
        variant="secondary"
        className="text-xs bg-green-50 text-green-700 border-green-200"
      >
        Loading...
      </Badge>
    );
  }

  if (!provider) {
    return null;
  }

  const dosLabel =
    treatmentRequest.service_start_date || treatmentRequest.service_end_date
      ? `${formatDateForDisplay(treatmentRequest.service_start_date ?? 'N/A')} — ${formatDateForDisplay(treatmentRequest.service_end_date ?? 'Present')}`
      : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="secondary"
          className="text-xs bg-green-50 text-green-700 border-green-200 max-w-full cursor-default gap-1 text-left"
        >
          <Heart className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{provider.name}</span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        <div className="text-center">
          <div>{provider.name}</div>
          {dosLabel && (
            <div className="mt-0.5 text-zinc-500">DOS: {dosLabel}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export const LinkedEntityBadge: React.FC<LinkedEntityBadgeProps> = ({
  coverage,
  medicalNote,
  policeReport,
  treatmentRequest,
  matterId: matterIdProp,
}) => {
  // Try to derive matterId from props first; fall back to route param if present
  const { matter_id: routeMatterId } = useParams<{ matter_id?: string }>();
  const matterId = matterIdProp || routeMatterId;

  if (coverage) {
    const propertyLabel = coverage.named_insured?.find((value) => value?.trim())?.trim();
    const displayName =
      coverage.category === CoverageCategory.PROPERTY
        ? (propertyLabel ?? 'Property Coverage')
        : (coverage.carrier?.name?.trim() || 'Coverage');
    const coverageUrl = matterId
      ? `/matters/${matterId}/coverage/${coverage.id}`
      : null;
    const policyNumber =
      coverage.policy_identifiers && coverage.policy_identifiers.length > 0
        ? coverage.policy_identifiers[0].identifier_value
        : null;

    const badge = (
      <Badge
        variant="secondary"
        className="text-[9px] font-medium bg-blue-50 text-blue-700 border-blue-200 max-w-full truncate cursor-pointer hover:bg-blue-100 transition-colors text-left gap-1"
      >
        <CreditCard className="h-2.5 w-2.5 shrink-0" />
        {displayName}
      </Badge>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {coverageUrl ? (
            <Link to={coverageUrl} target="_blank" rel="noopener noreferrer">
              {badge}
            </Link>
          ) : (
            badge
          )}
        </TooltipTrigger>
        <TooltipContent side="left">
          <div className="text-center">
            <div className="font-medium">Coverage</div>
            <div className="text-slate-300">{displayName}</div>
            {policyNumber && (
              <div className="text-xs text-slate-400 mt-1">
                Policy: {policyNumber}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }
  if (treatmentRequest) {
    return (
      <TreatmentRequestBadge
        treatmentRequest={treatmentRequest}
        matterId={matterId}
      />
    );
  }

  if (medicalNote) {
    const displayName = medicalNote.name || 'Medical Provider';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className="text-xs bg-green-50 text-green-700 border-green-200 max-w-[160px] truncate cursor-default gap-1"
          >
            <Heart className="h-2.5 w-2.5 shrink-0" />
            {displayName}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {displayName}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (policeReport) {
    const displayName =
      policeReport.report_identifier ||
      policeReport.police_department.name ||
      'Police Report';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className="text-xs bg-purple-50 text-purple-700 border-purple-200 max-w-[160px] truncate cursor-default"
          >
            {displayName}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {displayName}
        </TooltipContent>
      </Tooltip>
    );
  }

  return null;
};
